import styled from '@emotion/styled';
import {
  MissionType,
  TransactionsCSVUploadPayload,
  TransactionsCSVUploadSubtype,
} from '@finaloop/mission-types';
import { Button, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Moment } from 'moment';
import { useState } from 'react';
import { Company } from '../../../../../services/FinaloopGql';
import { useMissionsApiClient } from '../../../../common/hooks/useMissionsApiClient';
import { timeInMsToMonthName } from '../../../shared';
import { Bank } from '../../../types';

const InputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 10px;
  flex-wrap: wrap;
`;

const Summery = styled.div`
  background-color: #e7e7e7;
  border-radius: 10px;
  padding: 10px;
`;

const SuccessText = styled.label`
  color: green;
`;

const accountTypeToMissionAccountType: Record<string, 'bank' | 'credit'> = {
  ['Depository']: 'bank',
  ['CreditCard']: 'credit',
};

export const OnetimeCsvMission = ({
  selectedBank,
  company,
  selectedSubtype,
}: {
  selectedBank: Bank;
  company: Company;
  selectedSubtype: 'one-time' | 'one-time-reconciliation';
}) => {
  const [
    selectedStartingMonth,
    setSelectedStartingMonth,
  ] = useState<Moment | null>(null);
  const [selectedEndingMonth, setSelectedEndingMonth] = useState<Moment | null>(
    null
  );

  const { useTriggerMission } = useMissionsApiClient();
  const { isError, isLoading, isSuccess, triggerMission } = useTriggerMission();

  const fromTimeInMs = selectedStartingMonth
    ?.clone()
    .startOf('month')
    .valueOf();
  const toTimeInMs = selectedEndingMonth
    ?.clone()
    .startOf('month')
    .valueOf();

  const onSaveClicked = async () => {
    const payload: TransactionsCSVUploadPayload = {
      bankId: selectedBank.id,
      institution: {
        name: selectedBank.institutionName,
      },
      account: {
        name: selectedBank.name,
        type: accountTypeToMissionAccountType[selectedBank.type],
        mask: selectedBank.mask,
      },
      fromTimeInMs: selectedStartingMonth!
        .clone()
        .startOf('month')
        .valueOf(),
      toTimeInMs: selectedEndingMonth!
        .clone()
        .endOf('month')
        .valueOf(),
      subtype:
        selectedSubtype === 'one-time'
          ? TransactionsCSVUploadSubtype.oneTimeMissingTransactions
          : TransactionsCSVUploadSubtype.oneTimeReconciliation,
    };

    await triggerMission({
      type: MissionType.TransactionsCSVUpload,
      companyId: company.id,
      payload,
    });
  };

  const canSave =
    !!selectedStartingMonth &&
    !!selectedEndingMonth &&
    !!selectedBank &&
    !isLoading;

  return (
    <InputsContainer>
      <DatePicker
        inputFormat='yyyy-MM'
        views={['year', 'month']}
        label='From'
        value={selectedStartingMonth}
        onChange={setSelectedStartingMonth}
        renderInput={(params: any) => <TextField {...params} helperText={null} />}
      />
      <DatePicker
        inputFormat='yyyy-MM'
        views={['year', 'month']}
        label='To'
        value={selectedEndingMonth}
        onChange={setSelectedEndingMonth}
        renderInput={(params: any) => <TextField {...params} helperText={null} />}
      />
      {canSave && (
        <Summery>
          <label>
            {`Mission will be created once, right now, asking for a CSV upload for gaps from `}
            {fromTimeInMs && <b>{timeInMsToMonthName(fromTimeInMs)}</b>}
            {` to `}
            {toTimeInMs && <b>{timeInMsToMonthName(toTimeInMs)}</b>}
            <br />
            {` For company: `}
            <b>{company.name}</b>
            {`, Bank: `}
            <b>{selectedBank?.fullName}</b>.
          </label>
        </Summery>
      )}
      <Button disabled={!canSave} onClick={onSaveClicked}>
        Create Mission
      </Button>
      {isError && <>Something went wrong.</>}
      {isLoading && <>Creating a new mission...</>}
      {isSuccess && (
        <SuccessText>Mission was created successfully!</SuccessText>
      )}
    </InputsContainer>
  );
};
